import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  NATO_CONFUSERS,
  NATO_DIGITS,
  NATO_KEYS,
  NATO_LETTERS,
  canSpeak,
  speak,
  type PhoneticEntry,
} from "@nav-games/comms";
import {
  type Attempt,
  type SessionResult,
  SessionRecorder,
  pickWeightedKey,
  recordAttempt,
  statsFor,
} from "@nav-games/games-core";

import { HelpButton, HelpPanel } from "../../ui/HelpPanel";

interface NatoProps {
  onNavigate: (to: string) => void;
}

const MODULE = "nato";
const GAME = "flashcards";
const CARDS_PER_SESSION = 15;
const BEAT_THE_CLOCK_MS = 90_000;
const CHOICES_PER_CARD = 4;

type Direction = "char-to-word" | "word-to-char";
type SessionMode = "cards" | "timed";

function lookup(char: string): PhoneticEntry | undefined {
  return NATO_LETTERS[char] ?? NATO_DIGITS[char];
}

interface Choice {
  /// Text shown on the button.
  label: string;
  /// Whether picking this choice counts as the right answer.
  correct: boolean;
}

interface Card {
  /// NATO key the question is about (used for stats logging).
  answer: string;
  choices: readonly Choice[];
}

/// Pick the next card. `recent` carries the last few answers so we
/// avoid drawing the same letter twice in a short window — pure
/// uniform random over 36 keys gives 15-card sessions a noticeable
/// chance of repeats (birthday-paradox territory) which feels broken.
///
/// Char-to-word cards use the curated `NATO_CONFUSERS` list as
/// distractors — same-initial-letter plausible-but-wrong words like
/// "Fox, Fish, Flag" alongside "Foxtrot". Word-to-char cards stick
/// with random other letters since there's no equivalent confusion
/// pool for single characters.
function nextCard(direction: Direction, recent: readonly string[]): Card {
  const answer = pickWeightedKey(MODULE, NATO_KEYS, recent);
  const answerEntry = lookup(answer)!;

  if (direction === "char-to-word") {
    const pool = NATO_CONFUSERS[answer] ?? [];
    const decoys = shuffle(pool).slice(0, CHOICES_PER_CARD - 1);
    // If the confuser table is too short (digits only have 5), top
    // up with random other NATO words so we always have 4 buttons.
    while (decoys.length < CHOICES_PER_CARD - 1) {
      const filler = NATO_KEYS[Math.floor(Math.random() * NATO_KEYS.length)]!;
      const word = lookup(filler)!.word;
      if (filler !== answer && !decoys.includes(word)) decoys.push(word);
    }
    const choices = shuffle<Choice>([
      { label: answerEntry.word, correct: true },
      ...decoys.map((d) => ({ label: d, correct: false })),
    ]);
    return { answer, choices };
  }

  // word-to-char: pick CHOICES_PER_CARD-1 random other letters/digits.
  const taken = new Set<string>([answer]);
  const decoys: string[] = [];
  while (decoys.length < CHOICES_PER_CARD - 1) {
    const k = NATO_KEYS[Math.floor(Math.random() * NATO_KEYS.length)]!;
    if (!taken.has(k)) {
      taken.add(k);
      decoys.push(k);
    }
  }
  const choices = shuffle<Choice>([
    { label: answerEntry.char, correct: true },
    ...decoys.map((k) => ({ label: lookup(k)!.char, correct: false })),
  ]);
  return { answer, choices };
}

function shuffle<T>(input: readonly T[]): T[] {
  const arr = [...input];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const ai = arr[i]!;
    const aj = arr[j]!;
    arr[i] = aj;
    arr[j] = ai;
  }
  return arr;
}

export function Nato({ onNavigate }: NatoProps) {
  const [direction, setDirection] = useState<Direction | null>(null);
  const [sessionMode, setSessionMode] = useState<SessionMode | null>(null);

  if (!direction || !sessionMode) {
    return (
      <Picker
        onStart={(d, m) => {
          setDirection(d);
          setSessionMode(m);
        }}
        onBack={() => onNavigate("/play/mayday")}
      />
    );
  }
  return (
    <Game
      direction={direction}
      sessionMode={sessionMode}
      onAgain={() => {
        /* re-enter with same direction/mode */
      }}
      onBack={() => onNavigate("/play/mayday")}
    />
  );
}

function Picker({
  onStart,
  onBack,
}: {
  onStart: (d: Direction, m: SessionMode) => void;
  onBack: () => void;
}) {
  return (
    <>
      <h2>NATO phonetic alphabet</h2>
      <p className="muted">
        Pick a direction and session length. The same pool covers A–Z plus
        0–9 — digits are drilled with the ICAO/ITU pronunciations
        (Tree, Fife, Niner).
      </p>
      <div className="card">
        <h3>Char → code-word</h3>
        <p className="small muted">
          See a letter or digit, pick the code-word.
        </p>
        <div
          className="row"
          style={{ flexDirection: "column", gap: 8, alignItems: "stretch" }}
        >
          <button onClick={() => onStart("char-to-word", "cards")}>
            {CARDS_PER_SESSION} cards
          </button>
          <button
            className="secondary"
            onClick={() => onStart("char-to-word", "timed")}
          >
            Beat the clock — {Math.round(BEAT_THE_CLOCK_MS / 1000)} s
          </button>
        </div>
      </div>
      <div className="card">
        <h3>Code-word → char</h3>
        <p className="small muted">
          See a code-word, pick the letter or digit.
        </p>
        <div
          className="row"
          style={{ flexDirection: "column", gap: 8, alignItems: "stretch" }}
        >
          <button onClick={() => onStart("word-to-char", "cards")}>
            {CARDS_PER_SESSION} cards
          </button>
          <button
            className="secondary"
            onClick={() => onStart("word-to-char", "timed")}
          >
            Beat the clock — {Math.round(BEAT_THE_CLOCK_MS / 1000)} s
          </button>
        </div>
      </div>
      <button className="secondary" onClick={onBack}>
        ← Back to Mayday
      </button>
    </>
  );
}

interface GameProps {
  direction: Direction;
  sessionMode: SessionMode;
  onAgain: () => void;
  onBack: () => void;
}

function Game({ direction, sessionMode, onBack }: GameProps) {
  const recorder = useMemo(
    () =>
      new SessionRecorder({
        module: MODULE,
        game: GAME,
        length: sessionMode === "cards" ? CARDS_PER_SESSION : 0,
      }),
    [sessionMode],
  );

  // Last few answers, used to avoid back-to-back repeats. Tuned at
  // 8 for a 15-card session over 36 keys — long enough that the
  // player rarely sees the same key twice, short enough that the
  // full alphabet still gets touched.
  const recentRef = useRef<string[]>([]);
  const [index, setIndex] = useState(0);
  const [card, setCard] = useState<Card>(() => {
    const c = nextCard(direction, []);
    recentRef.current = [c.answer];
    return c;
  });
  const [shownAt, setShownAt] = useState<number>(() => Date.now());
  const [feedback, setFeedback] = useState<"right" | "wrong" | null>(null);
  const [result, setResult] = useState<SessionResult | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const sessionStartedAt = useRef<number>(Date.now());

  const [now, setNow] = useState<number>(Date.now());
  useEffect(() => {
    if (sessionMode !== "timed" || result) return;
    const t = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(t);
  }, [sessionMode, result]);
  const timeLeftMs = Math.max(
    0,
    BEAT_THE_CLOCK_MS - (now - sessionStartedAt.current),
  );
  useEffect(() => {
    if (sessionMode !== "timed" || result) return;
    if (timeLeftMs === 0) setResult(recorder.summarise());
  }, [timeLeftMs, sessionMode, result, recorder]);

  const advance = useCallback(() => {
    if (sessionMode === "cards" && index + 1 >= CARDS_PER_SESSION) {
      setResult(recorder.summarise());
      return;
    }
    setIndex((i) => i + 1);
    const next = nextCard(direction, recentRef.current);
    recentRef.current = [...recentRef.current, next.answer].slice(-8);
    setCard(next);
    setShownAt(Date.now());
    setFeedback(null);
  }, [direction, index, recorder, sessionMode]);

  const onChoose = useCallback(
    (choice: Choice) => {
      if (feedback || result) return;
      const ms = Date.now() - shownAt;
      const ok = choice.correct;
      const attempt: Omit<Attempt, "module"> = {
        char: card.answer,
        ok,
        ms,
        ts: Date.now(),
      };
      recorder.log(attempt);
      recordAttempt({ ...attempt, module: MODULE });
      setFeedback(ok ? "right" : "wrong");
      // Speak the code-word on reveal so the ear learns the
      // pronunciation alongside the spelling. We deliberately don't
      // await — the timeout below advances the card.
      const entry = lookup(card.answer);
      if (entry) void speak(entry.word);
      setTimeout(advance, ok ? 1500 : 2500);
    },
    [advance, card, feedback, recorder, result, shownAt],
  );

  if (result) {
    return <EndCard result={result} onBack={onBack} />;
  }

  const liveCorrect = recorder.summarise().correct;
  const header =
    sessionMode === "cards"
      ? `Card ${index + 1} of ${CARDS_PER_SESSION}`
      : `${Math.ceil(timeLeftMs / 1000)} s left · ${liveCorrect} right`;

  const entry = lookup(card.answer)!;
  const prompt = direction === "char-to-word" ? entry.char : entry.word;
  const promptLabel =
    direction === "char-to-word" ? "Letter / digit" : "Code-word";

  return (
    <>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
        <HelpButton onOpen={() => setHelpOpen(true)} />
      </div>
      <HelpPanel section={helpOpen ? "nato" : null} onClose={() => setHelpOpen(false)} />
      <p className="small muted">{header}</p>
      <div className="card" style={{ textAlign: "center" }}>
        <p className="small muted" style={{ marginTop: 0 }}>
          {promptLabel}
        </p>
        <p
          style={{
            fontSize: direction === "char-to-word" ? 64 : 32,
            margin: "8px 0 16px",
            fontWeight: 600,
            letterSpacing: "0.02em",
          }}
        >
          {prompt}
        </p>
        {feedback && (
          <div style={{ marginBottom: 12 }}>
            <p className="small muted" style={{ margin: "0 0 6px" }}>
              {feedback === "right" ? "✓ " : "✗ "}
              {entry.char} = {entry.word} ({entry.pronunciation})
            </p>
            {canSpeak() && (
              <button
                className="secondary"
                onClick={() => void speak(entry.word)}
                style={{ fontSize: 12, padding: "4px 10px" }}
                aria-label={`Speak ${entry.word}`}
              >
                🔊 Replay
              </button>
            )}
          </div>
        )}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 8,
          }}
        >
          {card.choices.map((c) => (
            <button
              key={c.label}
              className="secondary"
              disabled={!!feedback}
              onClick={() => onChoose(c)}
              style={{
                fontSize: direction === "char-to-word" ? 16 : 22,
                padding: "10px 8px",
                background:
                  feedback && c.correct
                    ? "rgba(44, 122, 59, 0.18)"
                    : undefined,
              }}
            >
              {c.label}
            </button>
          ))}
        </div>
      </div>
    </>
  );
}

function EndCard({
  result,
  onBack,
}: {
  result: SessionResult;
  onBack: () => void;
}) {
  // Filter on hitRate < 1 so we don't show 100% items in a list
  // labelled "Weakest" — that's just confusing. If every item the
  // player has touched is at 100% we hide the section entirely.
  const rolling = NATO_KEYS
    .map((id) => ({ id, ...statsFor(MODULE, id) }))
    .filter((s) => s.attempts >= 3 && s.hitRate < 1)
    .sort((a, b) => a.hitRate - b.hitRate);
  const weakest = rolling.slice(0, 5);

  return (
    <>
      <h2>Session complete</h2>
      <div className="card">
        <p>
          <strong>{result.correct}</strong> / {result.total} correct
          {result.total > 0 && (
            <> ({Math.round((result.correct / result.total) * 100)}%)</>
          )}
          .
        </p>
      </div>
      {weakest.length > 0 && (
        <div className="card">
          <h3>Weakest (rolling)</h3>
          <ul className="small muted">
            {weakest.map((w) => {
              const e = lookup(w.id);
              return (
                <li key={w.id}>
                  <code>{w.id}</code> — {e?.word ?? ""}:{" "}
                  {Math.round(w.hitRate * 100)}% over {w.attempts} attempts
                </li>
              );
            })}
          </ul>
        </div>
      )}
      <button className="secondary" onClick={onBack}>
        ← Back to Mayday
      </button>
    </>
  );
}
